/**
 * useEventStream — canonical, per-URL singleton SSE subscription for the platform.
 *
 * Promoted AS-IS from operations-manager's `useFabricStream` (the best existing
 * implementation) and generalized so every realtime surface shares one primitive
 * instead of re-rolling `new EventSource` a seventh time. Consumers get:
 *   - exactly ONE `EventSource` per URL per user, ref-counted (open on first
 *     subscriber, close on last) — the one-connection-per-user contract Fabric
 *     certified, now keyed per-URL so independent streams never collide on a socket;
 *   - `withCredentials: true` always (cookie auth via the BFF);
 *   - exponential reconnect backoff (cap 30s, max attempts), typed `connectionState`;
 *   - **stale-by-data-age**: the hook tracks the timestamp of the last event that
 *     *advanced the data* and derives `dataStale = now - lastFreshAt > N x cadence`,
 *     so a healthy heartbeat with a frozen number reads Stale, not Live (the pill
 *     never lies — see `StreamStatusPill`);
 *   - passive `Last-Event-ID` forwarding (opt-in via `resumable`; the endpoint need
 *     not implement replay);
 *   - hold-last-known on disconnect (last snapshot is retained, not cleared).
 *
 * Consumer-specific logic (edge mapping, ticker reducers, toast wiring) lives in the
 * caller — this hook returns raw named events, a per-type last-known map, and a
 * bounded event ring. A "thin wrapper" MAY add typed event names + URL construction;
 * it MAY NOT own reconnect, connection state, or its own `EventSource`.
 */

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback } = React;

export type StreamConnectionState =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error'
  | 'disconnected';

export interface StreamMessage {
  /** SSE event name (`event:` field), or `'message'` for unnamed events. */
  type: string;
  /** Parsed JSON payload (or `{}` if the frame was not JSON). */
  data: any;
  /** Raw event data string, verbatim. */
  raw: string;
  /** Server-assigned `id:` for this event, if any. */
  lastEventId: string | null;
  /** `Date.now()` when the event was received. */
  receivedAt: number;
}

export interface UseEventStreamOptions {
  /** Named SSE events to listen for (in addition to the default connection events). */
  events?: string[];
  /** When false, the hook does not open a connection. Default true. */
  enabled?: boolean;
  /** Send cookies with the SSE request. Default true (platform streams are cookie-auth). */
  withCredentials?: boolean;
  /** Max exponential-backoff reconnect attempts before surfacing `error`. Default 6. */
  maxReconnectAttempts?: number;
  /** Backoff delay ceiling. Default 30_000ms. */
  maxReconnectDelayMs?: number;
  /** Cap on the retained event ring (newest-first). Default 100. */
  maxBufferedEvents?: number;
  /**
   * Expected data cadence in ms. Enables stale-by-data-age when > 0. Default 0 (off).
   * Living Spend passes 5000 (the snapshot cadence).
   */
  cadenceMs?: number;
  /** Stale multiplier N: `dataStale` after `now - lastFreshAt > N x cadenceMs`. Default 3. */
  staleFactor?: number;
  /**
   * Which event types advance data-freshness. When set, ONLY these types count as
   * "the data moved" — so a spark/denial flurry with a frozen snapshot correctly
   * reads Stale. When null/omitted, every non-heartbeat event advances freshness.
   */
  freshnessEvents?: string[] | null;
  /** Event types that never advance freshness (and are kept out of the ring). */
  heartbeatEvents?: string[];
  /** Forward the last seen `id:` as a query param on manual reconnect. Default false. */
  resumable?: boolean;
  /** Query-param name used when `resumable`. Default `lastEventId`. */
  lastEventIdParam?: string;
}

export interface EventStreamSnapshot {
  connectionState: StreamConnectionState;
  /** Most recent NON-heartbeat event (heartbeats prove liveness but aren't "data"). */
  lastEvent: StreamMessage | null;
  /** Most recent event per type (last-known; survives disconnect; includes heartbeats). */
  lastEventByType: Record<string, StreamMessage>;
  /** Bounded ring of recent non-heartbeat events, newest first. */
  events: StreamMessage[];
  /** Monotonic counter bumped on every non-heartbeat event — cheap flare trigger (heartbeats excluded). */
  eventSeq: number;
  /** `Date.now()` of the last data-advancing event, or null. */
  lastFreshAt: number | null;
  /** True when the socket is fine but the data has not advanced within N x cadence. */
  dataStale: boolean;
  reconnectAttempt: number;
  error: string | null;
}

export interface UseEventStreamResult extends EventStreamSnapshot {
  /** Reset backoff and reconnect immediately (e.g. an error-state "Retry" button). */
  reconnect: () => void;
}

const DEFAULT_HEARTBEAT_EVENTS = ['connected', 'heartbeat', 'keepalive', 'ping'];
const DEFAULT_MAX_ATTEMPTS = 6;
const DEFAULT_MAX_DELAY_MS = 30_000;
const DEFAULT_MAX_BUFFER = 100;
const DEFAULT_STALE_FACTOR = 3;

const IDLE_SNAPSHOT: EventStreamSnapshot = Object.freeze({
  connectionState: 'disconnected',
  lastEvent: null,
  lastEventByType: {},
  events: [],
  eventSeq: 0,
  lastFreshAt: null,
  dataStale: false,
  reconnectAttempt: 0,
  error: null,
});

interface StreamConfig {
  events: string[];
  withCredentials: boolean;
  maxReconnectAttempts: number;
  maxReconnectDelayMs: number;
  maxBufferedEvents: number;
  cadenceMs: number;
  staleFactor: number;
  freshnessEvents: string[] | null;
  heartbeatEvents: string[];
  resumable: boolean;
  lastEventIdParam: string;
}

interface Store {
  url: string;
  cfg: StreamConfig;
  es: EventSource | null;
  refCount: number;
  reconnectAttempt: number;
  reconnectTimer: any;
  staleTimer: any;
  eventSeq: number;
  lastEventId: string | null;
  snapshot: EventStreamSnapshot;
  listeners: Set<() => void>;
}

// Per-URL singleton registry. Fabric and Spend get independent one-connection-per-user
// guarantees instead of colliding on one socket (the whole point of keying per-URL).
const g = globalThis as any;
const registry: Map<string, Store> = g.__eventStreams || (g.__eventStreams = new Map());

function buildConfig(options: UseEventStreamOptions): StreamConfig {
  return {
    events: options.events ? [...options.events] : [],
    withCredentials: options.withCredentials !== false,
    maxReconnectAttempts: options.maxReconnectAttempts ?? DEFAULT_MAX_ATTEMPTS,
    maxReconnectDelayMs: options.maxReconnectDelayMs ?? DEFAULT_MAX_DELAY_MS,
    maxBufferedEvents: options.maxBufferedEvents ?? DEFAULT_MAX_BUFFER,
    cadenceMs: options.cadenceMs ?? 0,
    staleFactor: options.staleFactor ?? DEFAULT_STALE_FACTOR,
    freshnessEvents: options.freshnessEvents ?? null,
    heartbeatEvents: options.heartbeatEvents ?? DEFAULT_HEARTBEAT_EVENTS,
    resumable: options.resumable ?? false,
    lastEventIdParam: options.lastEventIdParam ?? 'lastEventId',
  };
}

function getStore(url: string, options: UseEventStreamOptions): Store {
  let store = registry.get(url);
  if (!store) {
    store = {
      url,
      cfg: buildConfig(options),
      es: null,
      refCount: 0,
      reconnectAttempt: 0,
      reconnectTimer: null,
      staleTimer: null,
      eventSeq: 0,
      lastEventId: null,
      snapshot: { ...IDLE_SNAPSHOT, lastEventByType: {}, events: [] },
      listeners: new Set<() => void>(),
    };
    registry.set(url, store);
  }
  return store;
}

function emit(store: Store) {
  store.listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* isolate listener errors — one bad consumer must not break the stream */
    }
  });
}

function computeStale(store: Store): boolean {
  const { cadenceMs, staleFactor } = store.cfg;
  const { lastFreshAt } = store.snapshot;
  if (cadenceMs <= 0 || lastFreshAt == null) return false;
  return Date.now() - lastFreshAt > staleFactor * cadenceMs;
}

function patch(store: Store, partial: Partial<EventStreamSnapshot>) {
  store.snapshot = { ...store.snapshot, ...partial };
  emit(store);
}

function isHeartbeat(store: Store, type: string): boolean {
  return store.cfg.heartbeatEvents.indexOf(type) !== -1;
}

function advancesFreshness(store: Store, type: string): boolean {
  const { freshnessEvents } = store.cfg;
  if (freshnessEvents && freshnessEvents.length > 0) {
    return freshnessEvents.indexOf(type) !== -1;
  }
  // Default: any non-heartbeat event advances freshness.
  return !isHeartbeat(store, type);
}

function handleEvent(store: Store, type: string, ev: MessageEvent) {
  let data: any = {};
  const raw = typeof ev?.data === 'string' ? ev.data : '';
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    /* non-JSON frame (e.g. bare heartbeat) — keep empty object */
  }

  const receivedAt = Date.now();
  const lastEventId = (ev && (ev as any).lastEventId) || null;
  if (lastEventId) store.lastEventId = lastEventId;

  const msg: StreamMessage = { type, data, raw, lastEventId, receivedAt };
  const hb = isHeartbeat(store, type);

  // Every frame (incl. heartbeats) proves the socket is healthy and is exposed as the
  // last-known-of-type. Only NON-heartbeat frames advance the ring, `eventSeq`,
  // `lastEvent`, and data-freshness — so heartbeats never trigger consumer flares and
  // a heartbeat alone never makes a frozen number look Live.
  const next: Partial<EventStreamSnapshot> = {
    connectionState: 'connected',
    lastEventByType: { ...store.snapshot.lastEventByType, [type]: msg },
    reconnectAttempt: 0,
    error: null,
  };

  if (!hb) {
    store.eventSeq += 1;
    next.eventSeq = store.eventSeq;
    next.events = [msg, ...store.snapshot.events].slice(0, store.cfg.maxBufferedEvents);
    next.lastEvent = msg;
    if (advancesFreshness(store, type)) {
      next.lastFreshAt = receivedAt;
    }
  }

  // Recompute staleness against the (possibly) advanced freshness timestamp.
  store.snapshot = { ...store.snapshot, ...next };
  store.snapshot.dataStale = computeStale(store);
  emit(store);
}

function startStaleTimer(store: Store) {
  if (store.staleTimer || store.cfg.cadenceMs <= 0) return;
  const period = Math.max(1000, Math.floor(store.cfg.cadenceMs));
  store.staleTimer = (globalThis as any).setInterval(() => {
    const stale = computeStale(store);
    if (stale !== store.snapshot.dataStale) {
      patch(store, { dataStale: stale });
    }
  }, period);
}

function stopStaleTimer(store: Store) {
  if (store.staleTimer) {
    clearInterval(store.staleTimer);
    store.staleTimer = null;
  }
}

function buildUrl(store: Store): string {
  if (store.cfg.resumable && store.lastEventId) {
    const sep = store.url.indexOf('?') === -1 ? '?' : '&';
    return `${store.url}${sep}${encodeURIComponent(store.cfg.lastEventIdParam)}=${encodeURIComponent(store.lastEventId)}`;
  }
  return store.url;
}

function openConnection(store: Store) {
  if (store.es) return;
  if (typeof EventSource === 'undefined') {
    patch(store, { connectionState: 'error', error: 'EventSource unavailable' });
    return;
  }

  // Hold last-known: only surface reconnecting/connecting, never blank the data.
  patch(store, {
    connectionState: store.snapshot.lastEvent ? 'reconnecting' : 'connecting',
  });

  let es: EventSource;
  try {
    // The ONE place in the platform allowed to construct EventSource (ESLint
    // no-restricted-syntax exempts this file). Everything else uses this hook.
    es = new EventSource(buildUrl(store), { withCredentials: store.cfg.withCredentials });
  } catch (err: any) {
    patch(store, { connectionState: 'error', error: err?.message || 'EventSource failed' });
    return;
  }
  store.es = es;

  es.onopen = () => {
    store.reconnectAttempt = 0;
    patch(store, { connectionState: 'connected', reconnectAttempt: 0, error: null });
  };

  const listenSet = new Set<string>([...DEFAULT_HEARTBEAT_EVENTS, ...store.cfg.events]);
  listenSet.forEach((k) => {
    es.addEventListener(k, (e: any) => handleEvent(store, k, e as MessageEvent));
  });
  // Unnamed frames (`event:` absent) arrive on `message`.
  es.onmessage = (e: any) => handleEvent(store, 'message', e as MessageEvent);

  es.onerror = () => {
    es.close();
    if (store.es === es) store.es = null;
    if (store.refCount > 0 && store.reconnectAttempt < store.cfg.maxReconnectAttempts) {
      const delay = Math.min(
        1000 * Math.pow(2, store.reconnectAttempt),
        store.cfg.maxReconnectDelayMs,
      );
      store.reconnectAttempt += 1;
      patch(store, { connectionState: 'reconnecting', reconnectAttempt: store.reconnectAttempt });
      store.reconnectTimer = (globalThis as any).setTimeout(() => {
        if (store.refCount > 0) openConnection(store);
      }, delay);
    } else {
      patch(store, {
        connectionState: 'error',
        error: store.snapshot.error || 'Stream connection failed',
      });
    }
  };
}

function closeConnection(store: Store) {
  if (store.reconnectTimer) {
    clearTimeout(store.reconnectTimer);
    store.reconnectTimer = null;
  }
  stopStaleTimer(store);
  if (store.es) {
    store.es.close();
    store.es = null;
  }
  store.reconnectAttempt = 0;
  patch(store, { connectionState: 'disconnected', reconnectAttempt: 0 });
}

function subscribe(store: Store, listener: () => void): () => void {
  store.listeners.add(listener);
  store.refCount += 1;
  if (store.refCount === 1) {
    openConnection(store);
    startStaleTimer(store);
  }
  return () => {
    store.listeners.delete(listener);
    store.refCount -= 1;
    if (store.refCount <= 0) {
      store.refCount = 0;
      closeConnection(store);
      // Drop the empty store so per-run/per-URL streams don't accumulate forever.
      if (store.listeners.size === 0) registry.delete(store.url);
    }
  };
}

export interface EventStreamHandle {
  /** Detach this subscriber (ref-counted; closes the socket when it was the last). */
  unsubscribe: () => void;
  /** Current snapshot of the underlying per-URL stream. */
  getSnapshot: () => EventStreamSnapshot;
  /** Reset backoff and reconnect. */
  reconnect: () => void;
}

export interface SubscribeEventStreamOptions extends UseEventStreamOptions {
  /** Fired once per NEW non-heartbeat event (preserves "revalidate on message"). */
  onEvent?: (msg: StreamMessage, snapshot: EventStreamSnapshot) => void;
  /** Fired whenever the snapshot changes (connection state, staleness, events). */
  onChange?: (snapshot: EventStreamSnapshot) => void;
}

/**
 * Imperative subscription for consumers that manage a DYNAMIC set of streams (e.g. one
 * per workflow run) and therefore can't use the React hook (which is one-per-component).
 * Same per-URL singleton, backoff, credentials, and stale tracking as `useEventStream`.
 */
export function subscribeEventStream(
  url: string,
  options: SubscribeEventStreamOptions = {},
): EventStreamHandle {
  const store = getStore(url, options);
  let lastSeq = store.eventSeq;
  const listener = () => {
    if (options.onEvent && store.eventSeq !== lastSeq) {
      lastSeq = store.eventSeq;
      if (store.snapshot.lastEvent) options.onEvent(store.snapshot.lastEvent, store.snapshot);
    }
    if (options.onChange) options.onChange(store.snapshot);
  };
  const unsub = subscribe(store, listener);
  return {
    unsubscribe: unsub,
    getSnapshot: () => store.snapshot,
    reconnect: () => reconnectEventStream(url),
  };
}

/** Reset backoff and reconnect the stream for `url` (manual "Retry"). */
export function reconnectEventStream(url: string): void {
  const store = registry.get(url);
  if (!store) return;
  store.reconnectAttempt = 0;
  if (store.reconnectTimer) {
    clearTimeout(store.reconnectTimer);
    store.reconnectTimer = null;
  }
  if (store.es) {
    store.es.close();
    store.es = null;
  }
  if (store.refCount > 0) openConnection(store);
}

/** Test/introspection seam: current ref count for `url` (0 if no store). */
export function _eventStreamRefCount(url: string): number {
  return registry.get(url)?.refCount ?? 0;
}

/** Test-only: tear down and clear all streams (used between test cases). */
export function _resetEventStreams(): void {
  registry.forEach((store) => {
    if (store.reconnectTimer) clearTimeout(store.reconnectTimer);
    if (store.staleTimer) clearInterval(store.staleTimer);
    if (store.es) store.es.close();
  });
  registry.clear();
}

/**
 * React hook: subscribe to the per-URL singleton SSE stream. Every caller of the
 * same URL shares one connection.
 */
export function useEventStream(
  url: string | null | undefined,
  options: UseEventStreamOptions = {},
): UseEventStreamResult {
  const enabled = options.enabled !== false && !!url;

  // React is provided via globalThis (untyped), so cast rather than use generic args.
  const [snap, setSnap] = (useState as any)(() =>
    enabled ? getStore(url as string, options).snapshot : IDLE_SNAPSHOT,
  ) as [EventStreamSnapshot, (v: EventStreamSnapshot) => void];

  // Keep the latest options for store creation without retriggering the effect on
  // every render (options object identity churns; the stream identity is the URL).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!enabled) {
      setSnap(IDLE_SNAPSHOT);
      return;
    }
    const store = getStore(url as string, optionsRef.current);
    const unsub = subscribe(store, () => setSnap(store.snapshot));
    setSnap(store.snapshot); // sync any state that arrived before mount
    return unsub;
  }, [url, enabled]);

  const reconnect = useCallback(() => {
    if (enabled && url) reconnectEventStream(url);
  }, [url, enabled]);

  return { ...snap, reconnect };
}

export default useEventStream;
